import { APIProvider } from '@vis.gl/react-google-maps'
import { useTranslation } from 'next-i18next'
import React, { ComponentProps, useCallback, useEffect, useState } from 'react'
import ErrorBoundary from 'src/components/ErrorBoundary'
import config from 'src/config'
import { ICoordinates } from 'src/types/graphql'
import Map from '.'
import SpotMeMarker from './SpotMeMarker'
import { getStaticMapUrl } from './staticMapUrl'

type IPropsLoader = Omit<ComponentProps<typeof Map>, 'isLoaded'>

/**
 * Neutral gray block shown whenever the interactive map can't render — either
 * the SDK never loaded (`APIProvider onError`) or it threw mid-render and the
 * boundary in `MapWrapper` caught it. Identical markup for both paths so the
 * layout never shifts and the rest of the page stays usable.
 */
const MapUnavailable = () => {
  const { t } = useTranslation('common')

  return (
    <div
      className="flex size-full items-center justify-center bg-cream-300"
      role="img"
      aria-label={t('A11Y.LOAD_MAP')}
    />
  )
}

const MapLoader = ({
  previewWhileLoading = true,
  ...props
}: IPropsLoader & { previewWhileLoading?: boolean }) => {
  const { i18n } = useTranslation('common')
  const [isLoaded, setIsLoaded] = useState(false)
  const [hasError, setHasError] = useState(false)
  const { center, zoom, white } = props

  return (
    <APIProvider
      apiKey={config.firebaseClientCredentials.apiKey}
      onLoad={() => setIsLoaded(true)}
      onError={() => setHasError(true)}
      // Follow the active locale: this drives the Maps UI chrome AND every
      // control's aria-label. Hardcoding "es" left EN users with a fully
      // Spanish map ("Combinaciones de teclas", "Mover a la izquierda") —
      // an a11y problem, not just a copy one. Safe to read i18n here: the
      // map only ever mounts client-side, so there is no SSR/CSR drift.
      language={i18n.language === 'en' ? 'en' : 'es'}
    >
      {hasError ? (
        // APIProvider failed to load (rare — but happens when the user has
        // aggressive adblock/privacy extensions that block maps.googleapis.com,
        // when Chrome's tab-restoration races SDK init, or when a stale
        // service-worker entry serves a cached error). Don't show a raw
        // error string to the user — render the neutral gray block instead
        // so the layout doesn't shift and the page stays usable. The
        // graceful Maps-SDK fallback (raster tiles when only the styled
        // mapId fails) still kicks in when this branch is NOT hit.
        <MapUnavailable />
      ) : null}
      {/* The SDK is already downloading at this point — this is what the
          reader looks at while it does. Without it the container sat empty
          (or gray) for the whole ~1.5 MB fetch, which is why /search felt
          like it "popped in". Same style, centre and zoom as the map that
          replaces it, so the hand-off reads as the map sharpening rather
          than as one thing swapping for another.

          Free on a `deferUntilClick` surface: the button above renders the
          exact same URL, so this is a browser cache hit, not a second
          billed Static Maps request. */}
      {!isLoaded && !hasError && previewWhileLoading && center ? (
        <StaticMapLoading center={center} zoom={zoom} white={white} />
      ) : null}
      {isLoaded && !hasError && <Map {...props} />}
    </APIProvider>
  )
}

type IStaticPreviewProps = {
  center: ICoordinates
  zoom?: number
  white?: boolean
}

/**
 * The Static Maps image itself — one cacheable request, no JS SDK, no WebGL.
 *
 * `marker: false` — this stands in for the interactive map, and the
 * interactive map never draws a pin at the viewport centre. On /search (and
 * every city/hood page) the centre is a city centroid, so a pin there marks
 * nothing; on the listing page the centre is deliberately obfuscated, so a
 * hard pin overstates the precision the copy right below it disclaims.
 *
 * Returns `null` styling when the Static Maps key isn't configured, so the
 * element degrades to the neutral gray block and nothing breaks in an env
 * that hasn't been provisioned.
 *
 * Hydration note: `backgroundImage: url("...")` with the quoted form is
 * SSR/CSR-stable — the URL is a pure function of the (already-obfuscated)
 * center + zoom, so the serialized style attribute is byte-identical on both
 * sides (see spotme-client/CLAUDE.md → Trigger D Phase 6).
 */
const staticMapStyle = ({ center, zoom, white }: IStaticPreviewProps) => {
  const staticUrl = getStaticMapUrl({ center, zoom, white, marker: false })
  return staticUrl ? { backgroundImage: `url("${staticUrl}")` } : undefined
}

/**
 * Non-interactive preview, shown while the SDK downloads.
 *
 * Both images are built from the same `MAP_IDS` cloud style, centre and zoom,
 * so the hand-off to the interactive map is a near-identical repaint rather
 * than a visible swap — that continuity is the whole point of sharing
 * `mapIds.ts`. Nothing here is focusable: it is a loading state the map
 * replaces on its own, not something the reader can act on.
 */
const StaticMapLoading = ({ center, zoom, white }: IStaticPreviewProps) => {
  const { t } = useTranslation('common')
  const staticUrl = getStaticMapUrl({ center, zoom, white, marker: false })

  // A real <img>, not the `background-image` this used to be. A background is
  // only discovered once style resolution reaches the element, so it starts at
  // Low priority and cannot carry `fetchpriority` at all. On mobile city and
  // pillar pages this element IS the LCP, and Chrome measured 2,164 ms of pure
  // *load delay* on it (2026-08-31, /pension-estacionamiento/ciudad-de-mexico
  // on Slow 4G): the bytes took 3 ms, finding them took all the rest. An <img>
  // is visible to the preload scanner and takes the high-priority hint.
  if (!staticUrl) {
    return (
      <div
        className="size-full bg-cream-300"
        role="img"
        aria-label={t('A11Y.LOCATION')}
      />
    )
  }

  return (
    <img
      src={staticUrl}
      alt={t('A11Y.LOCATION')}
      // Not next/image: this is a Google Static Maps URL, already sized and
      // scaled by `staticMapUrl.ts`, and routing it through the optimizer
      // would add a Vercel image transform in front of the LCP resource —
      // the opposite of what this change is for.
      className="size-full bg-cream-300 object-cover object-center"
      fetchPriority="high"
      decoding="async"
    />
  )
}

/**
 * Click-to-load preview: the same image, plus a visible affordance so the
 * reader knows the surface is actionable.
 *
 * A real `<button>` rather than a `div[role=button]`: it gets Enter AND Space
 * for free (the hand-rolled `onKeyDown` here only ever handled Enter), plus
 * the correct focus semantics. The caption is the accessible name, so there
 * is no `aria-label` to drift away from the visible copy — and the marker is
 * `aria-hidden`, since announcing "SpotMe logo" before it would only get in
 * the way.
 */
const StaticMapButton = ({
  onClick,
  ...props
}: IStaticPreviewProps & { onClick: () => void }) => {
  const { t } = useTranslation('common')

  return (
    <button
      type="button"
      onClick={onClick}
      // Tailwind's preflight resets `background-image` on <button>, but the
      // inline style below outranks any stylesheet rule, so the preview still
      // paints; `bg-cover bg-center` position it. Focus lives on the marker
      // (see below) because a ring on this element is clipped by the
      // `overflow-hidden` container every caller wraps the map in.
      className="group flex size-full cursor-pointer flex-col items-center justify-center gap-3 bg-cream-300 bg-cover bg-center focus-visible:outline-none"
      style={staticMapStyle(props)}
    >
      {/* Our own round marker — the same blue disc and white mark as the pin
          Google bakes into a Static Maps image — so the affordance reads as
          something standing ON the map rather than chrome laid over it.
          `ring-offset` is what makes a blue focus ring visible against a blue
          disc: badge, white gap, ring. */}
      <span className="pointer-events-none flex size-14 items-center justify-center rounded-full bg-blue-500 shadow-[0_12px_28px_-8px_rgba(0,82,129,0.55)] transition-transform duration-200 group-hover:scale-[1.08] group-focus-visible:ring-2 group-focus-visible:ring-blue-800 group-focus-visible:ring-offset-2 group-active:scale-100 motion-reduce:transition-none motion-reduce:group-hover:scale-100">
        <SpotMeMarker className="w-8 text-white-500" />
      </span>
      {/* Deliberately quiet — the marker carries the invitation, this only
          says what a click gets you. It keeps a soft white backing because a
          map is not a uniform surface: over a park or a water body, bare
          low-contrast text would drop under 4.5:1. */}
      <span className="pointer-events-none rounded-full bg-white-500/85 px-3 py-1 text-[11px] font-medium tracking-[0.14em] text-ink-500 uppercase backdrop-blur-[2px]">
        {t('MAP.VIEW_INTERACTIVE')}
      </span>
    </button>
  )
}

type IPropsWrapper = IPropsLoader & {
  /**
   * Hold the ~1.5 MB Maps SDK back until the reader actually clicks the
   * preview. OFF by default, and off is the right answer on most surfaces:
   * every map still paints a static preview instantly and still swaps itself
   * to the interactive one with no interaction (see `MapLoader`) — this flag
   * only changes WHEN the SDK download starts.
   *
   * Set it where the map is supporting context the reader may never touch:
   * a listing's "where" section, /contacto, the chat drawer. Leave it OFF
   * wherever the map IS the page — /search and every city/hood page, the
   * wizard's coordinate picker, the listing editor. On those, making someone
   * tap before they can pan is a worse trade than the bytes are worth.
   *
   * When set, this deliberately has NO timer and NO scroll trigger. It used
   * to be `onScroll`, and `render` was
   * `scrolled || delayedMounted || userActivated` — a 3s timeout plus a
   * global window-scroll listener, so the SDK loaded on its own within
   * seconds no matter what. On the listing page that made the saving zero:
   * `Details/Location` only mounts this once an IntersectionObserver says the
   * user is scrolling toward the map, so the very next scroll event upgraded
   * it a frame later, and ~1.5 MB shipped on effectively every listing view
   * for a preview nobody saw. If a surface shouldn't need a click, take the
   * flag OFF — don't reintroduce an automatic trigger behind it. When the
   * surface has a real "the map is hidden right now" signal, use `deferWhile`
   * instead; that is a state gate, not a timer, which is why it's allowed
   * where the old `onScroll`/`delay` pair was not.
   */
  deferUntilClick?: boolean
  /**
   * Hold the ~1.5 MB Maps SDK back for as long as this is true — for a
   * surface that KNOWS the map is currently covered and can say when it
   * stops being.
   *
   * The case this exists for is mobile /search and every SSG city/pillar
   * page: `sliderOpen` defaults to `!!initialFilters` (FilterProvider), so
   * on those landing pages the results drawer starts OPEN, sitting on top of
   * the map. Downloading the SDK to power a map nobody can see costs the
   * reader the one thing that page is judged on — the static preview behind
   * the drawer is the LCP element, and it was losing the race to a 1.5 MB
   * script for bandwidth and main thread. Measured 2026-08-31: mobile p75
   * LCP on those routes went 2,950 ms → 4,040 ms when the SDK stopped being
   * deferred on 2026-08-26, with FCP flat — the signature of an LCP element
   * that got starved rather than a page that got heavier.
   *
   * One-way: once false, the SDK stays mounted even if the caller's
   * condition flips back (the drawer reopens). Unmounting a loaded map would
   * throw away the download AND the reader's pan/zoom.
   */
  deferWhile?: boolean
  /**
   * Paint the static preview while the SDK downloads. ON by default, and a
   * `deferUntilClick` surface should always leave it on — the click-to-load
   * button already rendered that exact URL, so the preview costs nothing but
   * a cache read.
   *
   * Turn it OFF only where the map loads eagerly AND the extra Static Maps
   * request isn't worth what it buys. That is desktop /search today (Jules,
   * 2026-08-26): the map is above the fold and would load anyway, so the
   * preview only sharpens perceived paint, and Static Maps bills per request
   * on our single highest-traffic surface. Mobile keeps it — it already made
   * that request under the old `onScroll` code, so mobile is cost-neutral
   * versus what shipped before, and it's where the slow connections are.
   */
  previewWhileLoading?: boolean
}

const MapWrapper = ({
  deferUntilClick,
  deferWhile = false,
  ...props
}: IPropsWrapper) => {
  const [hasMounted, setHasMounted] = useState<boolean>(false)
  const [userActivated, setUserActivated] = useState(false)
  // Latched, not derived: see `deferWhile`'s note. Seeded from the first
  // `deferWhile` so a surface that never defers behaves exactly as before —
  // no extra render, no delayed SDK.
  const [sdkReleased, setSdkReleased] = useState(!deferWhile)

  useEffect(() => {
    setHasMounted(true)
  }, [])

  useEffect(() => {
    if (sdkReleased || deferWhile) return
    setSdkReleased(true)
  }, [deferWhile, sdkReleased])

  const activate = useCallback(() => setUserActivated(true), [])

  // Guard on `center` too: with no centre there is nothing to draw a preview
  // of, and a gate that rendered null would leave a permanently blank box
  // with nothing to click. Fall through and mount the map instead.
  if (deferUntilClick && !userActivated && props.center) {
    return (
      <StaticMapButton
        center={props.center}
        zoom={props.zoom}
        white={props.white}
        onClick={activate}
      />
    )
  }

  // Held: the caller says the map is covered right now. Paint the static
  // preview and nothing else — no `APIProvider`, so the SDK download hasn't
  // started. Unlike every other branch here this one is NOT gated on
  // `hasMounted`, which is the point: rendering it on the server puts the LCP
  // image in the initial HTML where the preload scanner finds it, instead of
  // behind React hydration. The URL is a pure function of centre + zoom and
  // is documented SSR-safe in `staticMapUrl.ts`.
  if (!sdkReleased && props.center) {
    return (
      <StaticMapLoading
        center={props.center}
        zoom={props.zoom}
        white={props.white}
      />
    )
  }

  // Scoped boundary: the Maps SDK can throw DURING React's commit phase, which
  // `APIProvider onError` (a load-time callback) cannot see. Without this, such
  // a throw walks all the way to the app-root boundary in `_app.tsx` and
  // replaces the WHOLE page with <ClientError />. That is exactly what happened
  // on 2026-08-13: a `NotLoadingAPIFromGoogleMapsError` left the SDK
  // half-initialized, `marker.js setMap` then hit `undefined ... 'b.getRootNode'`
  // inside @vis.gl/react-google-maps, and a renter searching Monterrey parking
  // lost the entire /search page over a degraded map. A broken map must cost the
  // map, nothing more.
  return (
    <ErrorBoundary boundary="map" fallback={<MapUnavailable />}>
      {hasMounted && <MapLoader {...props} />}
    </ErrorBoundary>
  )
}

export default React.memo(MapWrapper)
